/** Bullets, thrown grenades, rockets, bombs and delayed cook-offs: flight, collision, detonation. */

import { type Ctx, isAlive, isFighter } from './ctx';
import { damageEnemy, destroyProp, explode, hitRanger, REFUSED } from './damage';
import { hullRadius, isDog } from './enemies/specials/kinds';
import { hullSpine } from './enemies/specials/vehicleHull';
import { flyThrown } from './grenades';
import { releaseSmoke } from './smoke';
import { firstBulletHit, segmentHit } from './statics';
import { BOMB, CARBINE, ENEMY, GEAR, GRENADE, LAW, RANGER, TACTICS } from './tuning';
import { WALLS } from './tuningWalls';
import type { Bullet, Enemy } from './types';
import { DOGS } from './tuningDogs';

export function spawnBullet(
  ctx: Ctx,
  fromPlayer: boolean,
  x: number,
  z: number,
  dirX: number,
  dirZ: number,
  range: number,
  ignoreProp: number,
  heavy: boolean,
  /** The ranger's suppressed pistol (04 §6): marked on the round and on the `shot` event. */
  silenced = false,
): void {
  const w = ctx.world;
  const bullet: Bullet = { id: w.nextId++, fromPlayer, x, z, prevX: x, prevZ: z, dirX, dirZ, range, ignoreProp, heavy };
  if (silenced) bullet.silenced = true;
  w.bullets.push(bullet);
  if (silenced) ctx.events.push({ type: 'shot', fromPlayer, x, z, dirX, dirZ, emplacement: heavy, suppressed: true });
  else ctx.events.push({ type: 'shot', fromPlayer, x, z, dirX, dirZ, emplacement: heavy });
}

/** What one of the ranger's rounds does to the man it strikes. */
function roundDamage(ctx: Ctx, b: Bullet, e: Enemy): number {
  if (b.silenced === true) {
    // The pistol's whole point: a man who never knew he was there goes down to one round, like the knife's (02 §6.3).
    const alerted = e.state === 'combat' || e.state === 'search' || e.state === 'down';
    const aware = alerted || e.detection >= GEAR.pistolAwareAt;
    if (!aware) return GEAR.pistolUnawareDamage;
  }
  return ctx.rng.chance(CARBINE.heavyHitChance) ? 2 : 1;
}

/**
 * Where along this tick's flight a rocket strikes `e`, or null for a clean miss.
 *
 * Every enemy used to be a 0.4 m circle at his own centre - a man's body - so a LAW fired at a
 * minitank whose tracks span +-0.79 m passed clean through the hull the player could see, kept its
 * fuse and flew on: "I tried to use a LAW on them but it went over it". A machine is now as wide as
 * its model, and a vehicle - far longer than it is wide - is answered by its capsule, so a rocket
 * into the flank of a 9.8 m locomotive strikes the locomotive and not the air beside its midpoint.
 */
export function rocketStrikes(e: Enemy, ax: number, az: number, bx: number, bz: number): number | null {
  const at = (x: number, z: number, r: number): number | null => {
    const hit = segmentHit(
      { propId: -1, shape: 'circle', x, z, r, hx: 0, hz: 0, h: 2, blocksMove: false, blocksSight: false, blocksBullets: true },
      ax, az, bx, bz,
    );
    return hit ? hit.enter : null;
  };
  const r = hullRadius(e.kind);
  if (e.vehicle === undefined) return at(e.x, e.z, r);

  // The capsule, sampled a radius at a time exactly as `hullBlocked` does: never misses the hull.
  const spine = hullSpine(e.kind, e.x, e.z, e.facing);
  const span = Math.sqrt((spine.bx - spine.ax) ** 2 + (spine.bz - spine.az) ** 2);
  const steps = Math.max(1, Math.ceil(span / r));
  let best: number | null = null;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const enter = at(spine.ax + (spine.bx - spine.ax) * t, spine.az + (spine.bz - spine.az) * t, r);
    if (enter !== null && (best === null || enter < best)) best = enter;
  }
  return best;
}

export function stepProjectiles(ctx: Ctx, dt: number, playerBulletSpeed: number): void {
  const w = ctx.world;
  const s = ctx.statics;

  for (let i = w.bullets.length - 1; i >= 0; i--) {
    const b = w.bullets[i];
    if (!b) continue;
    const step = Math.min(b.range, (b.fromPlayer ? playerBulletSpeed : ENEMY.bulletSpeed) * dt);
    const nx = b.x + b.dirX * step;
    const nz = b.z + b.dirZ * step;
    let tHit = 2;
    let victim: Enemy | null = null;
    let hitsRanger = false;
    const wall = firstBulletHit(s, b.x, b.z, nx, nz, b.ignoreProp);
    if (wall) tHit = wall.t;
    if (b.fromPlayer) {
      for (const e of w.enemies) {
        if (!isAlive(e)) continue;
        // A man lying down, or inside his bunker, is a much smaller target. A dog is always 'down' (dog.ts) and is
        // as big as it is (ROUND8).
        const radius = isDog(e.kind) ? DOGS.radius : e.state === 'down' ? ENEMY.radius * 0.6 : ENEMY.radius;
        const hit = segmentHit({ propId: -1, shape: 'circle', x: e.x, z: e.z, r: radius, hx: 0, hz: 0, h: 2, blocksMove: false, blocksSight: false, blocksBullets: true }, b.x, b.z, nx, nz);
        if (hit && hit.enter < tHit) {
          tHit = hit.enter;
          victim = e;
        }
      }
    } else {
      const r = w.ranger;
      const live = r.status === 'active' || r.status === 'downed' || r.status === 'boarding';
      // Rounds aimed at a standing man's chest pass over a crawler in a trench.
      const covered = r.inTrench && r.stance === 'crawl';
      if (live && !covered) {
        const hit = segmentHit({ propId: -1, shape: 'circle', x: r.x, z: r.z, r: RANGER.radius, hx: 0, hz: 0, h: 2, blocksMove: false, blocksSight: false, blocksBullets: true }, b.x, b.z, nx, nz);
        if (hit && hit.enter < tHit) {
          tHit = hit.enter;
          hitsRanger = true;
        }
      }
    }
    if (b.fromPlayer) {
      // Near misses make men duck: suppressive fire works (02 §9.4).
      for (const e of w.enemies) {
        if (!isFighter(e) || e === victim || e.persona === 'aggressive' || e.mans !== null) continue;
        const near = segmentHit({ propId: -1, shape: 'circle', x: e.x, z: e.z, r: TACTICS.nearMiss, hx: 0, hz: 0, h: 2, blocksMove: false, blocksSight: false, blocksBullets: true }, b.x, b.z, nx, nz);
        if (!near || near.enter > Math.min(1, tHit)) continue;
        const was = e.suppressTicks;
        e.suppressTicks = Math.max(e.suppressTicks, Math.round(TACTICS.suppressTicks * (e.persona === 'steady' ? 0.6 : 1)));
        if (was === 0 && e.state === 'combat') {
          e.tacticTicks = 0;
          ctx.events.push({ type: 'bark', id: e.id, x: e.x, z: e.z, kind: 'suppressed' });
        }
      }
    }
    if (tHit <= 1) {
      const hx = b.x + (nx - b.x) * tHit;
      const hz = b.z + (nz - b.z) * tHit;
      if (victim) damageEnemy(ctx, victim, roundDamage(ctx, b, victim), true);
      else if (hitsRanger) hitRanger(ctx, b.heavy);
      else {
        ctx.events.push({ type: 'impact', x: hx, z: hz, y: 1.0 });
        // HAZ: a searchlight's lamp is glass - the one fieldwork a carbine or pistol round puts out (02 §9.2).
        const struck = b.fromPlayer && wall ? s.def.props.find((p) => p.id === wall.collider.propId) : undefined;
        if (struck?.kind === 'searchlight') destroyProp(ctx, struck.id);
      }
      w.bullets.splice(i, 1);
      continue;
    }
    b.prevX = b.x;
    b.prevZ = b.z;
    b.x = nx;
    b.z = nz;
    b.range -= step;
    const out = nx < 0 || nz < 0 || nx > s.def.sizeX || nz > s.def.sizeZ;
    if (b.range <= 0 || out) {
      if (!out) ctx.events.push({ type: 'impact', x: nx, z: nz, y: 0 });
      w.bullets.splice(i, 1);
    }
  }

  for (let i = w.thrown.length - 1; i >= 0; i--) {
    const g = w.thrown[i];
    if (!g) continue;
    // In the air it flies an arc, and a wall or a building too tall to clear stops it (grenades.ts).
    if (g.flightTicks < g.flightTotal) flyThrown(ctx, g);
    else if (g.kind === 'smoke') {
      // No fuse and no blast: it bursts where it lands (smoke.ts).
      w.thrown.splice(i, 1);
      releaseSmoke(ctx, g.x, g.z);
    } else {
      g.y = 0.1;
      g.fuseTicks--;
      if (g.fuseTicks <= 0) {
        w.thrown.splice(i, 1);
        explode(ctx, g.x, g.z, GRENADE.radius, 'grenade', g.fromPlayer);
      }
    }
  }

  for (let i = w.rockets.length - 1; i >= 0; i--) {
    const k = w.rockets[i];
    if (!k) continue;
    const step = LAW.speed * dt;
    const nx = k.x + k.dirX * step;
    const nz = k.z + k.dirZ * step;
    let tHit = 2;
    // Faithful quirk: ANY solid stops a rocket - even a bush (01 §5). As a body, not a point: it must not thread
    // the slits between the pieces of an east or west wall face (`WALLS.body`).
    for (const c of s.colliders) {
      if (!c.blocksSight) continue;
      const hit = segmentHit(c, k.x, k.z, nx, nz, WALLS.body);
      if (hit && hit.enter < tHit) tHit = hit.enter;
    }
    for (const e of w.enemies) {
      if (!isAlive(e)) continue;
      const hit = rocketStrikes(e, k.x, k.z, nx, nz);
      if (hit !== null && hit < tHit) tHit = hit;
    }
    const armed = k.travelled >= LAW.armDistance;
    if (tHit <= 1 || k.travelled >= LAW.range) {
      const t = Math.min(1, tHit);
      const hx = k.x + (nx - k.x) * t;
      const hz = k.z + (nz - k.z) * t;
      w.rockets.splice(i, 1);
      if (armed) explode(ctx, hx, hz, LAW.radius, 'heavy', true);
      else {
        // A LAW arms `LAW.armDistance` metres out. Fired at something closer it bounces off inert -
        // correct, and until now completely silent: the panic shot at a charging tank simply did
        // nothing, with no way to learn why. The rule stays; it is said out loud now.
        ctx.events.push({ type: 'impact', x: hx, z: hz, y: 1 });
        ctx.events.push({ type: 'pickup', what: `${REFUSED} - THE LAW ARMS ${LAW.armDistance} METRES OUT` });
      }
      continue;
    }
    k.prevX = k.x;
    k.prevZ = k.z;
    k.x = nx;
    k.z = nz;
    k.travelled += step;
    if (nx < 0 || nz < 0 || nx > s.def.sizeX || nz > s.def.sizeZ) w.rockets.splice(i, 1);
  }

  for (let i = w.bombs.length - 1; i >= 0; i--) {
    const bomb = w.bombs[i];
    if (!bomb) continue;
    bomb.fuseTicks--;
    if (bomb.fuseTicks <= 0) {
      w.bombs.splice(i, 1);
      explode(ctx, bomb.x, bomb.z, BOMB.radius, 'bomb', true);
    }
  }

  for (let i = w.pendingBlasts.length - 1; i >= 0; i--) {
    const p = w.pendingBlasts[i];
    if (!p) continue;
    p.ticks--;
    if (p.ticks <= 0) {
      w.pendingBlasts.splice(i, 1);
      explode(ctx, p.x, p.z, p.radius, 'heavy', true);
    }
  }
}
